import useResizeObserver from "@react-hook/resize-observer";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { useShallow } from "zustand/react/shallow";

import { Feature, MultiPolygon, Polygon } from "@turf/helpers";
import * as turf from "@turf/turf";
import { Alert } from "antd";
import axios from "axios";
import { EVA_RESEARCH_NAME, IS_MCDAN_ENV } from "constants/app.constants";
import { MCDANIEL_NAME } from "constants/settings.constants";
import _debounce from "lodash/debounce";
import { RootState } from "store/rootReducer";
import {
  type ISelectedWell,
  selectAttentionWells,
  selectSelectedWells,
  selectTxnIdId,
  selectXdaInterceptData,
  selectXdaLineRequest,
  useMapStore
} from "stores/map";
import {
  selectWellCardPanelOpen,
  selectWellCardTargetUwi,
  useWellCardPanelStore
} from "stores/wellCardPanel";
import styled from "styled-components/macro";

import usePlayTo3DDataSources from "hooks/usePlayTo3DDataSources";
import useUserModules from "hooks/useUserModules";

import {
  ENTITIES_ON_MAP_QUERY_KEY,
  ENTITIES_ON_MAP_STALE_TIME,
  MAP_QUERY_CACHE_TIME,
  fetchEntitiesOnMap
} from "api/map";

import {
  KeyValue,
  ThreeDViewerOptionT,
  ThreeDViewerPersistedStateT,
  threeDSessionKey
} from "models";
import { ThreeDViewer as D3Viewer, HzWell, Mode } from "models/threed-viewer";

import { BusyOverlay, ErrorOverlay } from "components/activity/shared";
import getGeoMapLayers from "components/geo-map/hooks/getGeoMapLayers";
import { IconSpinner } from "components/icons";
import type { EntitiesInGroupModel } from "components/map/contexts/MapContextState";
import { useScreenshotContext } from "components/screenshot/hooks";
import { useHaveUserSettingsAnswered, useUserSettings } from "components/user/hooks";
import { Screenshot } from "components/vis";
import { buildThreeDPersistedState } from "components/vis/viewerPersistence";

import ThreeDVToolbar from "./ThreeDVToolbar";
import "./ThreeDViewer.scss";
import { useMapEntitiesQuery } from "./hooks";
import useGet3DScene, { Get3DSceneRequestT } from "./hooks/useGet3DScene";
import { buildWellsForScene, expandWellsForScene } from "./utils/buildWellsForScene";
import { isLassoFilterPolygon } from "./utils/isLassoFilterPolygon";
import { isDashedWell } from "./utils/isSubordinateWell";
import { updateThreeDViewerColours } from "./utils/updateThreeDViewerColours";
import { selectDimensions } from "./visDimensionsSelectors";
import { useVisDimensionsStore } from "./visDimensionsStore";

/**
 * The card's well as this scene holds it, or null if the scene has not drawn
 * it. The card names its well by the uwi the map and the scene request use,
 * and the scene keys on `UWI`, so either will do as a match.
 */
function findCardWell(hzWells: HzWell[], cardUwi: Maybe<string>): Maybe<HzWell> {
  if (!cardUwi) {
    return null;
  }
  return (
    hzWells?.find((well) => well.UnformattedUwi === cardUwi || well.UWI === cardUwi) ??
    null
  );
}

/**
 * The well the card is reading joins the wells drawn in the highlighted-well
 * black, for as long as the card is up. The list it joins is keyed on `UWI`,
 * so the well is looked up in the scene before it goes on.
 */
function withWellCardWell(
  hzWells: HzWell[],
  attentionWells: string[],
  cardUwi: Maybe<string>
): string[] {
  const cardWell = findCardWell(hzWells, cardUwi);
  if (!cardWell?.UWI || attentionWells.includes(cardWell.UWI)) {
    return attentionWells;
  }
  return [...attentionWells, cardWell.UWI];
}

function renderScene(
  self,
  hzWells,
  ipdb,
  vertical,
  options,
  resetCamera: boolean,
  attentionWells: string[] = []
) {
  if (!hzWells || !ipdb || !ipdb.Zones) {
    return;
  }
  const container = self.scene.current;
  if (!container) {
    return;
  }
  if (self.viewer == null) {
    self.viewer = new D3Viewer(
      container,
      options,
      self.inset.current,
      self.orientation.current
    );
    self.viewer.initialize();
  } else {
    self.viewer.container = container;
    self.viewer.onWindowResize();
    self.viewer.clearScene();
    self.viewer.updateOptions(options);
  }
  self.viewer.setData(hzWells, ipdb, vertical);

  // The preserve-camera path (resetCamera=false) keeps an absolute-world camera
  // offset that is only valid for the well set it was framed on. When the
  // rendered set changes (filter/line/selection) the stale offset can leave the
  // camera parked far from the new — often smaller — set, reading as a sudden
  // zoom-out. Force a reframe whenever the set of rendered UWIs differs from the
  // last framed set; preserve only for same-geometry re-renders (recolour,
  // option toggles, attention-well changes).
  const signature = hzWells
    .map((w) => w.UnformattedUwi)
    .sort()
    .join(",");
  const geometryChanged = signature !== self.lastFramedSignature;
  self.lastFramedSignature = signature;
  const shouldReset = resetCamera || geometryChanged;

  if (shouldReset) {
    self.viewer.resetCameraPosition(Mode.Well);
  }
  const focusWells = hzWells[Math.floor(hzWells.length / 2)];
  self.viewer.addIPDBPlane(ipdb);
  // Read off the instance rather than taken as an argument: the card's well
  // stays black through every render path, including the ones that re-render
  // the cached scene with no attention wells of their own.
  self.viewer.addHzWells(
    hzWells,
    withWellCardWell(hzWells, attentionWells, self.cardUwi)
  );
  self.viewer.addVerticalWells(vertical);
  self.viewer.pointCameraToWell(focusWells, shouldReset);
  container.style.visibility = "visible";
}

/**
 * `toolbarContainer` is for a host with a toolbar row of its own, such as the
 * well card panel: pass the element and this viewer's toolbar renders there,
 * always visible, instead of appearing over the scene on hover. Pass nothing
 * and a dashboard widget keeps that hover overlay unchanged.
 */
function ThreeDViewer({
  onFullscreenToggle,
  id,
  initialState,
  hideFullscreen = false,
  toolbarContainer = null,
  screenshotContainerId = "three-d-screenshot-overlay",
  screenshotWidgetId = "ml"
}: {
  onFullscreenToggle: (fullscreen: boolean) => void;
  id?: string;
  initialState?: ThreeDViewerPersistedStateT;
  hideFullscreen?: boolean;
  toolbarContainer?: Maybe<HTMLElement>;
  screenshotContainerId?: string;
  /**
   * Who this viewer is when its camera is pressed. The default is the viewer's
   * KIND, which every 3D viewer answers to - so a second one on the page went
   * into screenshot mode alongside the first. A host that mounts one beside a
   * widget passes its own.
   */
  screenshotWidgetId?: string;
}): JSX.Element {
  const containerRef = useRef(null);

  /*
   * Everything this viewer owns: its engine, the wells it last drew, and the
   * three boxes the engine draws into.
   *
   * It was one object at module scope, so two viewers on the page shared an
   * engine and a scene - the second to mount took the first one's canvases,
   * and the first to unmount disposed them for both. The boxes are refs on the
   * same object because the engine is built inside `renderScene`, which is
   * handed this and nothing else.
   */
  const instance = useRef({
    viewer: null,
    data: { hzWells: [], ipdb: [], control: null },
    /** The uwi of the open well card, or null when no card is open. */
    cardUwi: null as Maybe<string>,
    scene: { current: null },
    inset: { current: null },
    orientation: { current: null }
  });
  const threeDViewerData = instance.current;
  const { widget, settings } = useScreenshotContext();
  const { has3dGeoModel } = useUserModules();
  /*
   * The scene this viewer is asking for. It is state rather than an
   * imperative call so the answer lands in react-query's cache under the body
   * that asked for it: a second viewer wanting the same scene - the well card
   * open beside a 3D widget - joins the one request instead of posting its
   * own and racing it.
   */
  const [sceneRequest, setSceneRequest] = useState<Maybe<Get3DSceneRequestT>>(null);
  /*
   * What the request in flight was built from. The answer is drawn in an
   * effect, and the 600 ms debounce means the wells and options that asked
   * for it have often moved on by the time it lands.
   */
  const pendingSceneRef = useRef<{
    wellsForScene;
    options: ThreeDViewerOptionT;
    forceRefresh: boolean;
  }>(null);
  const {
    data: sceneResponse,
    error: sceneError,
    isFetching: isLoading
  } = useGet3DScene(sceneRequest);
  const { mapEntities } = useMapEntitiesQuery();
  const dimensions = useVisDimensionsStore(useShallow(selectDimensions));
  const { data: ipdbSources } = usePlayTo3DDataSources();
  const { data: mapLayers } = getGeoMapLayers();

  // state from store
  const selectedWells: { [name: string]: ISelectedWell } =
    useMapStore(selectSelectedWells);

  // Use ref to track current selectedWells for onOptionChange closure
  const selectedWellsRef = useRef(selectedWells);

  const txnIdId = useMapStore(selectTxnIdId);

  // Subscribe to the same entitiesOnMap cache key that XdaViewer uses.
  // React Query deduplicates the request — no extra network call when both
  // viewers are mounted simultaneously.
  //
  // Not gated by useShouldFetchEntitiesOnMap(): that hook's fetch policy is
  // based on the 2D map widget's own zoom/well-count, which is unrelated to
  // this viewer's state and would silently disable its entity coloring
  // whenever the 2D map (if present on the same dashboard) is zoomed out.
  const { data: entitiesOnMap } = useQuery<Record<string, EntitiesInGroupModel>>(
    [ENTITIES_ON_MAP_QUERY_KEY, txnIdId],
    () => fetchEntitiesOnMap(txnIdId),
    {
      enabled: !!txnIdId,
      staleTime: ENTITIES_ON_MAP_STALE_TIME,
      cacheTime: MAP_QUERY_CACHE_TIME
    }
  );

  // Flat uwi → color lookup derived from entitiesOnMap. Mirrors the same
  // computation in XdaViewer so group-mode well colors match the map.
  const groupModeUwiColors = useMemo<Record<string, string>>(() => {
    if (!entitiesOnMap) return {};
    const lookup: Record<string, string> = {};
    for (const group of Object.values(entitiesOnMap)) {
      for (const [uwi, color] of Object.entries(group.uwis)) {
        lookup[uwi] = color;
      }
    }
    return lookup;
  }, [entitiesOnMap]);

  // Ref so the stale debounce closure always reads current group colors.
  const groupModeUwiColorsRef = useRef(groupModeUwiColors);

  // Ref so updateScene (captured in a stale debounce closure) can always read
  // the latest mapEntities when filtering wells by the active filter.
  const mapEntitiesRef = useRef(null);

  // Tracks whether a filter change (new txnId) is pending a scene re-fetch.
  // Set to true when txnId changes; cleared once mapEntities resolves with new data.
  const pendingFilterRefreshRef = useRef(false);
  const lastSeenTxnIdRef = useRef(txnIdId);

  // Detect global group mode from Redux (same pattern as XdaViewer).
  // A ref mirrors the reactive value so the stale-closure inside
  // updateSceneDebounce (created once via useRef) always reads the
  // current flag without needing to be recreated on every render.
  const isMapGroupMode = useSelector((state: RootState) =>
    Boolean(state.groupBy.globalGroupBy?.mode)
  );
  const isMapGroupModeRef = useRef(isMapGroupMode);

  const userSettings = useUserSettings();
  // Whether the settings READ has come back, which is a different question from
  // whether this user has a saved geo record — see the persist effect below.
  const haveUserSettingsAnswered = useHaveUserSettingsAnswered();

  const mcdanielGroup = [
    {
      label: EVA_RESEARCH_NAME,
      options: [{ label: EVA_RESEARCH_NAME, value: MCDANIEL_NAME }]
    }
  ];

  const screenshotBounds = {
    width: settings?.width || 1152,
    height: settings?.height || 681
  };

  const screenshotOverlayVisible = useMemo(() => {
    return widget?.widgetId === screenshotWidgetId;
  }, [widget, screenshotWidgetId]);

  const [error, setError] = useState<string>(undefined);
  const [isLineOutOfView, setIsLineOutOfView] = useState(false);

  const xdaLineRequest = useMapStore(selectXdaLineRequest);
  const xdaInterceptData = useMapStore(selectXdaInterceptData);
  const xdaLineRequestRef = useRef(xdaLineRequest);
  const currentPolygon = useSelector((state: RootState) => state.filter.polygonFilter) as
    | Polygon
    | MultiPolygon
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    | Feature<Polygon | MultiPolygon, { [name: string]: any }>;
  const currentPolygonRef = useRef(currentPolygon);

  const checkLineVisibility = useCallback(
    (polygon: typeof currentPolygon, lineReq: typeof xdaLineRequest) => {
      if (!polygon || !lineReq.line || lineReq.line.length < 2) {
        setIsLineOutOfView(false);
        return false;
      }

      // A lasso filter polygon is not a map viewport — skip the out-of-view check
      if (isLassoFilterPolygon(polygon)) {
        setIsLineOutOfView(false);
        return false;
      }

      const line = turf.lineString([...lineReq.line]);
      const lineOutOfView =
        !turf.booleanWithin(line, polygon) && !turf.booleanCrosses(line, polygon);

      const interceptData = selectXdaInterceptData(useMapStore.getState());
      const interceptPoints = interceptData?.points ?? [];
      const wellsOutOfView =
        !lineOutOfView &&
        interceptPoints?.length > 0 &&
        !interceptPoints.some((pt) =>
          turf.booleanPointInPolygon(turf.point([pt[0], pt[1]]), polygon)
        );

      const outOfView = lineOutOfView || wellsOutOfView;
      setIsLineOutOfView(outOfView);
      if (outOfView) {
        threeDViewerData.viewer?.clearScene();
      } else if (threeDViewerData.data?.hzWells?.length) {
        renderScene(
          threeDViewerData,
          threeDViewerData.data.hzWells,
          threeDViewerData.data.ipdb,
          threeDViewerData.data.control,
          optionsRef.current,
          false,
          []
        );
      }

      return outOfView;
    },
    []
  );

  const checkLineVisibilityDebounce = useRef(
    _debounce((polygon: typeof currentPolygon, lineReq: typeof xdaLineRequest) => {
      checkLineVisibility(polygon, lineReq);
    }, 500)
  ).current;

  //use refs to always check visiblity of the current line + polygon
  useEffect(() => {
    xdaLineRequestRef.current = xdaLineRequest;
  }, [xdaLineRequest]);

  useEffect(() => {
    isMapGroupModeRef.current = isMapGroupMode;
  }, [isMapGroupMode]);

  useEffect(() => {
    groupModeUwiColorsRef.current = groupModeUwiColors;
  }, [groupModeUwiColors]);

  useEffect(() => {
    currentPolygonRef.current = currentPolygon;
  }, [currentPolygon]);

  function onError(message: string) {
    if (typeof message === "string") {
      setError(message);
    } else {
      setError("an error occurred");
    }
  }

  const attentionWells = useMapStore(selectAttentionWells) as string[];

  /*
   * The well the card is reading, or null when no card is open. Both viewers a
   * page can hold - the card's own Gunbarrel one and a dashboard widget - read
   * the same panel store, so the well is black in either.
   */
  const isWellCardOpen = useWellCardPanelStore(selectWellCardPanelOpen);
  const wellCardTargetUwi = useWellCardPanelStore(selectWellCardTargetUwi);
  const cardUwi = isWellCardOpen ? wellCardTargetUwi : null;

  const onOptionChange = useCallback((option: KeyValue, refreshData: boolean) => {
    optionsRef.current = Object.assign({}, optionsRef.current, option);
    setOptions(optionsRef.current);
    if ("ipdbField" in option && threeDViewerData.viewer && threeDViewerData.data) {
      threeDViewerData.viewer.setData(
        threeDViewerData.data.hzWells,
        threeDViewerData.data.ipdb,
        threeDViewerData.data.control
      );
    }
    if (refreshData === true) {
      updateSceneDebounce(selectedWellsRef.current, optionsRef.current, true);
    }
  }, []);

  // Track whether modelSource/ipdbSource were seeded from saved state so the
  // geoSettings effect does not override user-chosen values with user defaults.
  // Only true when initialState explicitly carries a modelSource — an empty or
  // partial initialState (e.g. old DB save predating persistence) must not block
  // user defaults from being applied.
  const hasRestoredSourcesRef = useRef(initialState?.modelSource !== undefined);

  // Compute user-default sources synchronously — mirrors XdaSettingsStore's defaultTopsModelSource
  // pattern. This ensures the correct value is used from the very first render and from the
  // very first sessionStorage write, closing the race window where a DashboardPane remount
  // (triggered by any dashboard context update) reads the stale "McDaniel" from session
  // before the geoSettings effect has a chance to correct it.
  // Named userGeoSettings to avoid shadowing the local `const geoSettings` inside the
  // live-update effect below, which has its own narrower scope.
  const userGeoSettings = userSettings?.defaults?.geoSettings;

  const [options, setOptions] = useState(() => {
    // Lazy initializer — runs only on the first render, avoiding wasted
    // recomputation of these defaults on every subsequent render.
    const defaultModelSource =
      !hasRestoredSourcesRef.current && userGeoSettings?.surfaceSource
        ? userGeoSettings.surfaceSource
        : MCDANIEL_NAME;
    const defaultIpdbSource =
      !hasRestoredSourcesRef.current && userGeoSettings?.ipdbSource
        ? userGeoSettings.ipdbSource
        : MCDANIEL_NAME;
    return {
      scaleZ: initialState?.scaleZ ?? 10,
      showDrainage: initialState?.showDrainage ?? false,
      showIPDBDrainage: initialState?.showIPDBDrainage ?? false,
      showVerticalPortion: initialState?.showVerticalPortion ?? false,
      lineWidth: initialState?.lineWidth ?? 4,
      showIPDBPlane: initialState?.showIPDBPlane ?? true,
      showIPDBMesh: initialState?.showIPDBMesh ?? false,
      highlightHzLeg: initialState?.highlightHzLeg ?? false,
      showMultiLegWells: initialState?.showMultiLegWells ?? true,
      hzLegColor: initialState?.hzLegColor ?? 0xd8b365,
      wireframe: initialState?.wireframe ?? false,
      showWellLabel: initialState?.showWellLabel ?? false,
      ipdbSource: initialState?.ipdbSource ?? defaultIpdbSource,
      ipdbSources: [],
      showWellDetail: initialState?.showWellDetail ?? false,
      colorMap: initialState?.colorMap ?? "rainbow",
      colorMapMin: initialState?.colorMapMin ?? null,
      colorMapMax: initialState?.colorMapMax ?? null,
      ipdbField: initialState?.ipdbField ?? "Porosity",
      ipdbFieldUnit: initialState?.ipdbFieldUnit ?? "%",
      rightLateralDistance: Math.round(dimensions.rightWidth),
      upVerticalDistance: Math.round(dimensions.upHeight),
      leftLateralDistance: Math.round(dimensions.leftWidth),
      downVerticalDistance: Math.round(dimensions.downHeight),
      maxValue: initialState?.maxValue ?? 2000,
      modelSource: initialState?.modelSource ?? defaultModelSource,
      modelSources: mcdanielGroup,
      scaleDrainage: initialState?.scaleDrainage ?? false,
      onOptionChange,
      showVertical: initialState?.showVertical ?? has3dGeoModel,
      availableWells: Object.keys(selectedWells)
    };
  });

  const optionsRef = useRef(options);

  // Persist serializable options to sessionStorage whenever they change so
  // the dashboard save mechanism picks them up via getSessionData.
  //
  // The two sources are held back until the user's settings read has answered,
  // and nothing else is. Both fall back to McDaniel while that read is out, and
  // this viewer now carries an `id`, so its first commit would write the
  // fallback as though the user had chosen it. Every later mount in the session
  // reads it back, takes it for saved state, and skips the effect below that
  // applies the user's own surface source — leaving the viewer on McDaniel for
  // the rest of the session. The XDA seed had this same bug and is fixed the
  // same way: a value nobody has chosen yet must not be recorded as a choice.
  //
  // Held back rather than the whole write withheld, because a wait that never
  // ends is the worse failure: the dashboard widget shares this key, and its
  // scaleZ, colour map and the rest have to survive a dashboard switch whatever
  // the settings read is doing. Restored state carries real choices already and
  // waits for nothing.
  useEffect(() => {
    if (!id) return;
    const persistable = buildThreeDPersistedState(
      options,
      hasRestoredSourcesRef.current,
      haveUserSettingsAnswered
    );
    sessionStorage.setItem(threeDSessionKey(id), JSON.stringify(persistable));
  }, [id, options, haveUserSettingsAnswered]);

  // Update options.onOptionChange whenever onOptionChange changes
  useEffect(() => {
    if (optionsRef.current.onOptionChange !== onOptionChange) {
      optionsRef.current = { ...optionsRef.current, onOptionChange };
      setOptions(optionsRef.current);
    }
  }, [onOptionChange]);

  useEffect(() => {
    onOptionChange(
      {
        ...optionsRef.current,
        rightLateralDistance: Math.round(dimensions.rightWidth ?? 100),
        upVerticalDistance: Math.round(dimensions.upHeight ?? 10),
        leftLateralDistance: Math.round(dimensions.leftWidth ?? 100),
        downVerticalDistance: Math.round(dimensions.downHeight ?? 10)
      },
      true
    );
  }, [dimensions]);
  function updateScene(
    selectedWells,
    options: ThreeDViewerOptionT,
    forceRefresh = false
  ) {
    // Derive the flat uwi → well payload for the scene. Refs are read here (not
    // captured) so the stale debounce closure always sees current values.
    const wellsForScene = buildWellsForScene({
      selectedWells,
      mapEntities: mapEntitiesRef.current,
      isMapGroupMode: isMapGroupModeRef.current,
      xdaLineWells: xdaLineRequestRef.current?.wells ?? [],
      groupModeUwiColors: groupModeUwiColorsRef.current
    });

    if (Object.keys(wellsForScene).length === 0) {
      threeDViewerData.data = { ...threeDViewerData.data, hzWells: [], ipdb: [] };
      if (threeDViewerData.viewer) {
        threeDViewerData.viewer.clearScene();
      }
      pendingSceneRef.current = null;
      setSceneRequest(null);
      return;
    }

    if (!forceRefresh) {
      //just update the color

      const data = threeDViewerData.data;
      if (!data) {
        return;
      }
      const hzWells: HzWell[] = data.hzWells;
      data.hzWells = hzWells.map((well) => {
        const foundWell = wellsForScene[well.UnformattedUwi];
        if (foundWell) {
          well.Color = parseInt(foundWell.color.substr(1), 16);
          well.IsSubordinateWell = isDashedWell(foundWell.group);
        }
        return well;
      });
      renderScene(
        threeDViewerData,
        data.hzWells,
        data.ipdb,
        data.control,
        options,
        false,
        attentionWells
      );
    }

    setError("");
    /*
     * Hand the request to the shared query rather than posting from here. An
     * identical body from the other viewer inside the debounce window is the
     * same cache key, so the two resolve to one POST; the answer is drawn in
     * the effect below.
     */
    pendingSceneRef.current = { wellsForScene, options, forceRefresh };
    setSceneRequest({ selectedWells: wellsForScene, options });
  }

  const updateSceneDebounce = useRef(
    _debounce((selectedWells, options: ThreeDViewerOptionT, forceRefresh = false) => {
      updateScene(selectedWells, options, forceRefresh);
    }, 600)
  ).current;

  /*
   * Draw whatever the shared query answered with. The fetch is shared between
   * viewers; the drawing is not, so the wells are copied out of the cached
   * response before this viewer stamps its own colours and subordinate flags
   * on them.
   */
  useEffect(() => {
    if (!sceneResponse) {
      return;
    }
    const pending = pendingSceneRef.current;
    if (!pending) {
      return;
    }
    if (sceneResponse.status !== 200) {
      onError(sceneResponse.data);
      return;
    }

    const hzWells: HzWell[] = sceneResponse.data?.hzWells ?? [];
    const data = {
      ...sceneResponse.data,
      hzWells: hzWells.map((well) => {
        const foundWell = pending.wellsForScene[well.UnformattedUwi];
        return foundWell
          ? { ...well, IsSubordinateWell: isDashedWell(foundWell.group) }
          : { ...well };
      })
    };

    threeDViewerData.data = data;
    renderScene(
      threeDViewerData,
      data.hzWells,
      data.ipdb,
      data.control,
      pending.options,
      !pending.forceRefresh,
      attentionWells
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sceneResponse]);

  useEffect(() => {
    if (!sceneError) {
      return;
    }
    // A request the query aborted because this viewer's inputs moved on is
    // not something to show.
    // eslint-disable-next-line import/no-named-as-default-member
    if (axios.isCancel(sceneError) || sceneError?.name === "CanceledError") {
      onError("");
      return;
    }
    // The service's own message on a failed scene, which is what the viewer
    // puts on screen.
    if (sceneError.response?.data) {
      onError(sceneError.response.data as string);
    } else {
      onError("network error occurred");
    }
    // `sceneRequest` is here so that asking again for a body that is already
    // in the cache in a failed state puts the message back: `updateScene`
    // clears the banner before every ask, and with only the error in the deps
    // nothing would raise it again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sceneError, sceneRequest]);

  useEffect(() => {
    selectedWellsRef.current = selectedWells;
  }, [selectedWells]);

  useEffect(() => {
    const expanded = expandWellsForScene(selectedWells);
    // In group mode selectedWells is empty; use line-intersecting UWIs so the
    // toolbar has a populated well list matching what the scene will render.
    const availableWells =
      Object.keys(expanded).length > 0
        ? Object.keys(expanded)
        : isMapGroupMode
        ? [...(xdaLineRequest.wells ?? [])]
        : [];
    // Update only the toolbar's well list here (refreshData=false). The
    // updateScene call below already fetches + renders the new selection; passing
    // refreshData=true would schedule a second, redundant debounced fetch.
    onOptionChange({ availableWells }, false);
    updateScene(selectedWells, optionsRef.current);
  }, [selectedWells, isMapGroupMode]);

  useEffect(() => {
    checkLineVisibilityDebounce.cancel();
    const isLassoFilter = isLassoFilterPolygon(currentPolygon);
    if (currentPolygon && !isLassoFilter && xdaLineRequest.line?.length >= 2) {
      const line = turf.lineString([...xdaLineRequest.line]);
      const lineOutOfView =
        !turf.booleanWithin(line, currentPolygon) &&
        !turf.booleanCrosses(line, currentPolygon);
      setIsLineOutOfView(lineOutOfView);
      if (lineOutOfView) {
        threeDViewerData.viewer?.clearScene();
      }
    } else {
      setIsLineOutOfView(false);
    }
    if (xdaLineRequest.line?.length >= 2) {
      updateSceneDebounce(selectedWellsRef.current, optionsRef.current, true);
    }
  }, [xdaLineRequest]);

  // Rerun visibility check on pan/zoom
  useEffect(() => {
    checkLineVisibilityDebounce(currentPolygon, xdaLineRequest);
  }, [currentPolygon]);

  useEffect(() => {
    if (!xdaInterceptData || !currentPolygonRef.current) return;
    checkLineVisibility(currentPolygonRef.current, xdaLineRequestRef.current);
  }, [xdaInterceptData]);

  useEffect(() => {
    return () => checkLineVisibilityDebounce.cancel();
  }, []);

  function refreshScene(data, attentionWells: string[] = []) {
    if (!data?.hzWells) return;

    renderScene(
      threeDViewerData,
      data.hzWells,
      data.ipdb,
      data.control,
      options,
      false,
      attentionWells
    );
  }

  /*
   * Open the card, page it to another well, or close it, and the cached scene
   * is drawn again with the new highlight. Only the line colours change, so
   * this is a redraw of the wells already in hand - no scene is asked for.
   *
   * Every 3D viewer on the page reads the card, a dashboard widget included,
   * and a redraw is a full rebuild of the scene. So a scene holding neither
   * the well that is giving the highlight up nor the one taking it is left
   * alone: it would be drawn exactly as it already is, and a widget showing
   * other wells must not flicker each time the card is paged.
   */
  useEffect(() => {
    const previous = threeDViewerData.cardUwi;
    threeDViewerData.cardUwi = cardUwi;
    const hzWells = threeDViewerData.data?.hzWells ?? [];
    if (!findCardWell(hzWells, previous) && !findCardWell(hzWells, cardUwi)) {
      return;
    }
    refreshScene(threeDViewerData.data, attentionWells);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardUwi]);

  // In group mode, re-render the cached scene whenever groupModeUwiColors
  // updates (i.e., a new txnId arrives with fresh group colours).
  // Mirrors the identical pattern in XdaViewer — renderScene is intentionally
  // called directly so it captures the current groupModeUwiColors closure
  // rather than the stale debounce version.
  useEffect(() => {
    if (!isMapGroupMode || !threeDViewerData.data?.hzWells?.length) return;
    threeDViewerData.data.hzWells = threeDViewerData.data.hzWells.map((well) => {
      const color = groupModeUwiColors[well.UnformattedUwi];
      if (color) {
        well.Color = parseInt(color.substr(1), 16);
      }
      return well;
    });
    refreshScene(threeDViewerData.data, attentionWells);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupModeUwiColors]);

  useEffect(() => {
    if (mapEntities?.data && threeDViewerData.data?.hzWells?.length) {
      // Subordinate wells (leg/inj) aren't returned by the filter endpoint, so
      // map each to its parent's UWI to recolour it from the parent's colour.
      const parentByUwi: Record<string, string> = {};
      for (const well of Object.values(selectedWellsRef.current ?? {})) {
        if (well.parentUwi) parentByUwi[well.Uwi] = well.parentUwi;
      }
      threeDViewerData.data.hzWells = updateThreeDViewerColours(
        threeDViewerData.data.hzWells,
        mapEntities.data,
        parentByUwi
      );
      const updated = { ...selectedWellsRef.current };
      const entityColorByUwi: Record<string, string> = {};
      for (const entity of mapEntities.data) {
        entityColorByUwi[entity.uwi] = entity.color;
        if (updated[entity.uwi]) {
          updated[entity.uwi] = { ...updated[entity.uwi], color: entity.color };
        }
      }
      // Keep subordinate wells in sync with their parent's new colour so the
      // refetch path (which rebuilds the payload from selectedWells) matches.
      for (const [uwi, well] of Object.entries(updated)) {
        const parentColor = well.parentUwi && entityColorByUwi[well.parentUwi];
        if (parentColor) {
          updated[uwi] = { ...well, color: parentColor };
        }
      }
      selectedWellsRef.current = updated;
    }
    refreshScene(threeDViewerData.data, attentionWells);
  }, [options, attentionWells, mapEntities]);

  // Keep mapEntitiesRef current and re-fetch the scene only when the active
  // filter changes (new txnId). Selection changes are already handled by the
  // useEffect([selectedWells]) above — firing here too would cause a redundant
  // API call on every well select/deselect.
  useEffect(() => {
    if (txnIdId !== lastSeenTxnIdRef.current) {
      lastSeenTxnIdRef.current = txnIdId;
      pendingFilterRefreshRef.current = true;
    }
    mapEntitiesRef.current = mapEntities?.data ?? null;
    const hasWells = Object.keys(selectedWellsRef.current ?? {}).length > 0;
    const hasLine = xdaLineRequestRef.current?.line?.length >= 2;
    // mapEntities query is disabled when no wells are selected; for line-only mode
    // use entitiesOnMap (always enabled when txnId exists) as the readiness gate.
    const isReady = hasWells ? !!mapEntities?.data : hasLine && !!entitiesOnMap;
    if (pendingFilterRefreshRef.current && isReady) {
      pendingFilterRefreshRef.current = false;
      if (hasWells || hasLine) {
        updateSceneDebounce(selectedWellsRef.current, optionsRef.current, true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // txnIdId is tracked via lastSeenTxnIdRef; other deps are stable refs or the
    // debounce handle — intentionally omitted to avoid selection-change re-runs
  }, [mapEntities, txnIdId, entitiesOnMap]);

  // Build the modelSources options list whenever mapLayers changes.
  // Does NOT apply user-default source selections — that is handled by the
  // geoSettings effect below, which has the correct deps to avoid the race
  // condition where mapLayers resolves before user settings are loaded.
  useEffect(() => {
    if (!userSettings?.geoModelSettings?.organizationEnabled) return;
    const orgGroups = {
      label: "Organization",
      options: []
    };
    if (mapLayers?.length > 0) {
      for (const item of mapLayers) {
        if (
          orgGroups.options.findIndex((o) => o.value === item.group) === -1 &&
          item.hasLsdMap
        ) {
          orgGroups.options.push({ label: item.group, value: item.group });
        }
      }
    }

    // if user is using mcdan env, make sure to show mcdan sources first
    const modelSources = IS_MCDAN_ENV
      ? [...mcdanielGroup, orgGroups]
      : [orgGroups, ...mcdanielGroup];

    onOptionChange({ ...optionsRef.current, modelSources }, false);
  }, [mapLayers, userSettings?.geoModelSettings?.organizationEnabled]);

  // Apply user-default modelSource and ipdbSource once user settings have
  // loaded, but only when there is no DB/session saved state to restore.
  useEffect(() => {
    if (hasRestoredSourcesRef.current) return; // saved state — don't override
    const geoSettings = userSettings?.defaults?.geoSettings;
    if (!geoSettings?.id) return; // settings not yet loaded
    onOptionChange(
      {
        ...optionsRef.current,
        modelSource: geoSettings.surfaceSource ?? optionsRef.current.modelSource,
        ipdbSource: geoSettings.ipdbSource ?? optionsRef.current.ipdbSource
      },
      true
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    userSettings?.defaults?.geoSettings?.id,
    userSettings?.defaults?.geoSettings?.surfaceSource,
    userSettings?.defaults?.geoSettings?.ipdbSource
  ]);

  useEffect(() => {
    if (!ipdbSources?.length) return;
    onOptionChange({ ...optionsRef.current, ipdbSources: ipdbSources }, true);
  }, [ipdbSources]);

  /*
   * No refetch of the data sources on mount. `usePlayTo3DDataSources` is a
   * shared query key, so subscribing to it is enough: it fetches for the
   * first subscriber and hands the cached answer to every later one. The
   * forced refetch only added a second call to the same endpoint every time a
   * viewer mounted, and it pushed the result at every other subscriber on the
   * page rather than at the one that asked.
   */
  useEffect(() => {
    const self = threeDViewerData;
    return () => {
      if (self?.viewer) {
        self.viewer.clearScene();
        self.viewer.clearEntireSceneAndDisposeRenderer();
        self.viewer = null;
      }
    };
  }, []);

  useEffect(() => {
    if (threeDViewerData?.viewer) {
      //give time for the container size to update before calling resize
      setTimeout(() => {
        if (threeDViewerData?.viewer) {
          threeDViewerData.viewer.onWindowResize();
        }
      }, 900);
    }
  }, [screenshotBounds]);

  function resetCamera() {
    const data = threeDViewerData.data;
    if (!data || !data.hzWells) {
      return;
    }
    renderScene(
      threeDViewerData,
      data.hzWells,
      data.ipdb,
      data.control,
      options,
      true,
      attentionWells
    );
  }

  const handleFullscreenToggle = (v) => {
    // refresh scene with new bounds
    if (threeDViewerData?.viewer) {
      //give time for the container size to update before calling resize
      setTimeout(() => {
        threeDViewerData?.viewer?.onWindowResize();
      }, 900);
    }
    onFullscreenToggle(v);
  };

  const handleScreenshotToggle = (v) => {
    handleFullscreenToggle(v);
  };

  useEffect(() => {
    handleScreenshotToggle(screenshotOverlayVisible);
  }, [screenshotOverlayVisible]);

  useEffect(() => {
    if (threeDViewerData?.viewer) {
      const rafId = requestAnimationFrame(() => {
        threeDViewerData?.viewer?.onWindowResize();
      });
      return () => cancelAnimationFrame(rafId);
    }
  }, [screenshotOverlayVisible]);

  // The renderer only re-reads its box on a window resize, so a container that
  // changes size on its own (a panel being maximized, for instance) leaves the
  // canvas at its old aspect.
  useResizeObserver(containerRef, () => {
    threeDViewerData?.viewer?.onWindowResize();
  });

  const threeDToolbar = (
    <ThreeDVToolbar
      mouseoverRef={containerRef}
      onFullscreenToggle={handleFullscreenToggle}
      resetCamera={resetCamera}
      options={options}
      hideFullscreen={hideFullscreen}
      embedded={!!toolbarContainer}
      hostElement={toolbarContainer}
      screenshotWidgetId={screenshotWidgetId}
    />
  );

  /*
   * Only the DOM position moves. The toolbar stays in this component's React
   * tree, so the screenshot context and every toggle's own provider still
   * reach it through the portal.
   */
  const hostToolbar = (toolbar: JSX.Element) =>
    toolbarContainer ? createPortal(toolbar, toolbarContainer) : toolbar;

  return (
    <Wrapper
      className="three-d-viewer"
      ref={containerRef}
      data-testid="widget-threed"
      isScreenshot={screenshotOverlayVisible}>
      <Screenshot
        key="screenshot"
        containerId={screenshotContainerId}
        screenshotWidgetId={screenshotWidgetId}
      />

      <ThreeDViewerContainer
        className="three-d-viewer-container"
        isScreenshot={screenshotOverlayVisible}
        screenshotWidth={screenshotBounds.width}
        screenshotHeight={screenshotBounds.height}>
        <ScreenshotContainer id={screenshotContainerId} />
        {/* Held by ref rather than named by id. The ids were fixed strings, so
            a second viewer on the page drew into the first one's boxes. */}
        <SceneContainer ref={threeDViewerData.scene} hidden={isLineOutOfView} />
        <OrientationContainer
          ref={threeDViewerData.orientation}
          hidden={isLineOutOfView}
        />
        <InsetContainer ref={threeDViewerData.inset} hidden={isLineOutOfView} />
      </ThreeDViewerContainer>

      {!screenshotOverlayVisible && hostToolbar(threeDToolbar)}
      {isLoading && (
        <BusyOverlay onContextMenu={(e) => e.preventDefault()}>
          <IconSpinner />
        </BusyOverlay>
      )}
      {!Object.keys(selectedWells).length && !error && !xdaLineRequest.line?.length && (
        <ErrorOverlay>
          <Alert
            type="warning"
            message={`No lines drawn. Please use line tool on the map toolbar to draw a cross section.`}
          />
        </ErrorOverlay>
      )}
      {isLineOutOfView && !error && (
        <ErrorOverlay>
          <Alert
            type="warning"
            message="Cross section line is outside the map view. Pan back to the line or draw a new one."
          />
        </ErrorOverlay>
      )}
      {error && (
        <ErrorOverlay>
          <Alert type="error" message={`Unable to render: ${error}`} />
        </ErrorOverlay>
      )}
    </Wrapper>
  );
}

export default ThreeDViewer;

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  background-color: inherit;

  ${(props) =>
    props.isScreenshot &&
    `
    display: grid;
    background-color: rgba(46, 72, 88, 0.24);
  `}
`;

const ThreeDViewerContainer = styled.div`
  width: 100%;
  height: 100%;
  background-color: inherit;

  ${(props) =>
    props.isScreenshot &&
    `
    justify-self: center;
    width: ${props.screenshotWidth}px;
    height: ${props.screenshotHeight}px;
    background-color: white;
    top: 60px;
  `}
`;

const SceneContainer = styled.div`
  width: 100%;
  height: 100%;
`;

const InsetContainer = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
`;

const ScreenshotContainer = styled.div`
  position: absolute;
  display: grid;
  justify-content: center;
  pointer-events: none;
`;

const OrientationContainer = styled.div`
  position: absolute;
  bottom: 0;
  right: 10px;
`;
